import { Injectable, inject } from '@angular/core'

import { capitalize } from 'lodash'

import { EMPTY, Observable, catchError, from, fromEvent, map, throwError } from 'rxjs'
import { filter, switchMap, tap } from 'rxjs/operators'

import { FlxModalService } from '@flowx/angular-ui-toolkit'
import { SseMessageType } from '@flowx/core-sdk'
import { EnumerationParams } from '@flowx/designer/shared/entities/enumeration.entity'
import { EnumerationsCsvService } from '@flowx/designer/shared/services/enumerations-csv.service'
import { ToastMessageService } from '@flowx/designer/shared/services/toast-message.service'
import { csvToBlob, loadLocalFileWithCancel } from '@flowx/shared/utils'

import { CopyResourceApi } from '../api/copy-resource.api'
import { ImportExportApiService } from '../api/import-export.api'
import { FlxExistingResourcesModalComponent } from '../components/existing-resources-modal/existing-resources-modal.component'
import {
  ApplicationResourceType,
  ApplicationResourceTypePlurals,
} from '../entities/application.entity'
import {
  ImportDecision,
  ImportResourceSessionResponse,
  ImportSseConnectionsMessages,
} from '../entities/existing-resource.entity'
import { CopyResourceSseMessageType } from '../model/copy-resource.model'

@Injectable({
  providedIn: 'root',
})
export class ImportExportService {
  #importExportApiService = inject(ImportExportApiService)
  #copyResourceApiService = inject(CopyResourceApi)
  #toastMessageService = inject(ToastMessageService)
  #enumerationsCsvService = inject(EnumerationsCsvService)
  #modalService = inject(FlxModalService)
  // TODO - this is the property that should be available for long running tasks (upload that takes allot of time)
  // and should be used to show progress bar or some other indication of the progress
  // #sseConnections: ImportSseConnections = {}
  // get sseConnections(): ImportSseConnections {
  //   return this.#sseConnections
  // }

  exportToZip(
    resourceType: ApplicationResourceType,
    resourceId?: string,
    _interactionSource?: 'pageAction' | 'rowAction'
  ): Observable<Blob> {
    void _interactionSource
    return this.#importExportApiService
      .export({ resourcesIds: resourceId ? [resourceId] : [] }, resourceType)
      .pipe(
        map((response) => new Blob([response], { type: 'application/zip' })),
        catchError((error) => {
          const parsedError = error.error?.defaultMessage ? ': ' + error.error?.defaultMessage : '.'

          this.#toastMessageService.showErrorMessage(
            `Failed to export ${ApplicationResourceTypePlurals[resourceType]}${parsedError}`
          )

          return EMPTY
        })
      )
  }

  importCsv(resourceType: ApplicationResourceType, file: File): Observable<any> {
    return this.#enumerationsCsvService
      .parseEnumerationsCSV(file)
      .pipe(
        switchMap((contents) =>
          this.#importExportApiService.importCsv(resourceType, { contents, exportVersion: 1 })
        )
      )
  }

  exportCsv(
    resourceType: ApplicationResourceType,
    languages: string[],
    sourceSystems: string[],
    params?: Partial<EnumerationParams>
  ): Observable<Blob> {
    return this.#importExportApiService.exportCsv(resourceType, params).pipe(
      switchMap((result) =>
        from(
          this.#enumerationsCsvService.transformJsonEnumerationCSV(result, languages, sourceSystems)
        )
      ),
      map((csv) => csvToBlob(csv))
    )
  }

  importZipWithExistingResourcesCheck(
    applicationResourceType: ApplicationResourceType
  ): Observable<void | ImportSseConnectionsMessages> {
    return loadLocalFileWithCancel({ accept: ['application/zip'] }).pipe(
      switchMap((files: FileList) => {
        if (!files) {
          return throwError(() => new Error())
        }

        const formData = new FormData()
        formData.append('file', files[0])

        return this.#importExportApiService.import(formData, applicationResourceType).pipe(
          catchError(({ error }) => {
            this.#toastMessageService.showErrorMessage(
              `${capitalize(ApplicationResourceTypePlurals[applicationResourceType])} import error.`,
              error?.defaultMessage || 'Error importing file.'
            )

            return throwError(() => new Error())
          })
        )
      }),
      switchMap((data) => {
        const modal$ = this.#modalService.show(FlxExistingResourcesModalComponent, {}, { ...data })

        if (data.conflicts.length) {
          return modal$.data$.pipe(
            switchMap((modalData) =>
              this.#handleModalDecision(data.sessionId, applicationResourceType, modalData)
            ),
            catchError(({ error }) => {
              this.#modalService.hide()

              this.#toastMessageService.showErrorMessage(
                `${capitalize(ApplicationResourceTypePlurals[applicationResourceType])} import error.`,
                error?.defaultMessage || 'Error importing file.'
              )

              return throwError(() => new Error())
            })
          )
        }

        return this.#returnSseConnection(data.sessionId, applicationResourceType)
      })
    )
  }

  #handleModalDecision(
    sessionId: string,
    applicationResourceType: ApplicationResourceType,
    {
      decision,
      updatedImportResourceSession,
    }: { decision: ImportDecision; updatedImportResourceSession: ImportResourceSessionResponse }
  ): Observable<void | ImportSseConnectionsMessages> {
    if (decision === ImportDecision.Abort) {
      this.#modalService.hide()
      return this.#importExportApiService.abortImport(sessionId)
    }
    return this.#importExportApiService
      .resumeImport(updatedImportResourceSession)
      .pipe(
        switchMap(() =>
          this.#returnSseConnection(updatedImportResourceSession.sessionId, applicationResourceType)
        )
      )
  }

  #returnSseConnection(
    sessionId: string,
    applicationResourceType: ApplicationResourceType
  ): Observable<{ status: CopyResourceSseMessageType; errorMessage: string }> {
    const connection = this.#copyResourceApiService.initSseConnection(sessionId)

    return fromEvent<MessageEvent<{ status: CopyResourceSseMessageType; errorMessage: string }>>(
      connection,
      SseMessageType.Data
    ).pipe(
      filter(Boolean),
      map((event: MessageEvent) => JSON.parse(event.data)),
      tap((data) => {
        connection.close()
        // delete this.#sseConnections[applicationResourceType]
        this.#showImportMessage(data, applicationResourceType)
        this.#modalService.hide()
      })
    )
    // this.#sseConnections[applicationResourceType] = connectionObservable
  }

  #showImportMessage(
    data: ImportSseConnectionsMessages,
    applicationResourceType: ApplicationResourceType
  ): void {
    if (data.status === CopyResourceSseMessageType.SUCCESS) {
      this.#toastMessageService.showSuccessMessage(
        `${capitalize(ApplicationResourceTypePlurals[applicationResourceType])} imported successfully.`
      )
      return
    }
    this.#toastMessageService.showErrorMessage(
      `${capitalize(ApplicationResourceTypePlurals[applicationResourceType])} import error.`,
      data.errorMessage
    )
  }
}
